import { MAINNET_MERKLE_LP } from '@aries/aptos-defi/config';
import { createTokenHub } from '@aries/defi-toolkit/token-hub';
import { Big, EMPTY_ASSET } from '@aries/defi-toolkit/types';
import { mapValues } from 'lodash';
import { ariesApi } from '../api';
import {
  getAptosNodeRequestHeaders,
  getProviderHub,
  Network,
  onNetworkChange,
} from '../aptos-provider';
import { fetchTokenList } from './fetcher';

export const APTOS_COIN_ADDRESS = '0x1::aptos_coin::AptosCoin';

export const { useTokenInfoHub, getTokenInfoHub } = createTokenHub(() => {
  const isTestnet = getProviderHub()?.env.isTestnet;
  return fetchTokenList(!!isTestnet);
});

const fetchCurrentNetworkResource = async (
  currentNetwork: NonNullable<
    ReturnType<typeof getProviderHub>
  >['currentNetwork'],
  resourcePath: string,
) => {
  const restBaseUrl = currentNetwork.nodeUrl.replace(/\/$/, '');
  const response = await fetch(`${restBaseUrl}/${resourcePath}`, {
    headers: getAptosNodeRequestHeaders(currentNetwork),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${resourcePath} from ${currentNetwork.name}: ${response.status}`,
    );
  }

  return response.json();
};

let tAPTrefreshTimer: NodeJS.Timeout | undefined;
onNetworkChange(currentNetwork => {
  clearInterval(tAPTrefreshTimer);

  const isTestnet = currentNetwork.network === Network.TESTNET;
  getTokenInfoHub()?.refreshTokenList();

  const loadBackendPrices = async () => {
    try {
      const oraclePriceMap = await ariesApi.coinInfo.currentInfo.query();

      if (!isTestnet) {
        try {
          const vaultData = await fetchCurrentNetworkResource(
            currentNetwork,
            'accounts/0x5ae6789dd2fec1a9ec9cccfb3acaf12e93d432f0a3a42c92fe1a9d490b7bbc06/resource/0x5ae6789dd2fec1a9ec9cccfb3acaf12e93d432f0a3a42c92fe1a9d490b7bbc06::vault::Vault<0x5ae6789dd2fec1a9ec9cccfb3acaf12e93d432f0a3a42c92fe1a9d490b7bbc06::vault_type::HouseLPVault,0x5ae6789dd2fec1a9ec9cccfb3acaf12e93d432f0a3a42c92fe1a9d490b7bbc06::fa_box::W_USDC>',
          );
          const vaultValue = Number(vaultData.data.coin_store.value);

          const supplyData1 = await fetchCurrentNetworkResource(
            currentNetwork,
            'accounts/0x5ae6789dd2fec1a9ec9cccfb3acaf12e93d432f0a3a42c92fe1a9d490b7bbc06/resource/0x1::coin::CoinInfo<0x5ae6789dd2fec1a9ec9cccfb3acaf12e93d432f0a3a42c92fe1a9d490b7bbc06::house_lp::MKLP<0x5ae6789dd2fec1a9ec9cccfb3acaf12e93d432f0a3a42c92fe1a9d490b7bbc06::fa_box::W_USDC>>',
          );
          const supply1 = Number(
            supplyData1.data.supply.vec[0].integer.vec[0].value,
          );

          const supplyData2 = await fetchCurrentNetworkResource(
            currentNetwork,
            'accounts/0x149125399662d1403e9e155cec665d68fc839e0100e9f3d0a4a1ad31f93b739e/resource/0x1::fungible_asset::ConcurrentSupply',
          );
          const supply2 = Number(supplyData2.data.current.value);

          const totalSupply = supply1 + supply2;

          const lpPrice = Big(vaultValue)
            .div(totalSupply)
            .mul(1000000)
            .round(0, Big.roundDown)
            .div(1000000)
            .toNumber();

          oraclePriceMap[MAINNET_MERKLE_LP] = {
            price: lpPrice,
            decimal: 6,
          };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch MAINNET_MERKLE_LP price:', error);
        }
      }
      getTokenInfoHub()?.setCustomizedPriceMap(
        mapValues(oraclePriceMap, v => v.price),
      );
      // eslint-disable-next-line no-empty
    } catch {}
  };

  loadBackendPrices();
  tAPTrefreshTimer = setInterval(() => {
    loadBackendPrices();
  }, 15000);
});

export const EMPTY_TOKEN = {
  ...EMPTY_ASSET,
  address: '',
};
